import { BestResults } from 'src/app/models/winco/best-results.model';
import { environment } from '../../environments/environment';
import { BetterScore } from '../models/winco/better-score.model';
import { Injectable } from '@angular/core';
import { Replay } from '../models/winco/replay.model';
import { ReplayCheckList } from '../models/winco/replay-check-list.model';
import { SimpleResponse } from 'http-manager';
import { ExportForm } from '../models/winco/export-form.model';
import { HttpClientService } from './http-client.service';
import { Parameter } from '../models/winco/parameter.model';
import { ReplayAnalyzerComponent } from '../components/replay-analyzer/replay-analyzer.component';
import { ReplayViewerAnalysisComponent } from '../components/replay-viewer-analysis/replay-viewer-analysis.component';
import { ModalBoxClose, ModalBoxData, ModalBoxEvent, ModalBoxService } from 'modal-box';
import { ReplayAnalizerExportComponent } from '../components/replay-analizer-export/replay-analizer-export.component';
import { LaparoscopyAnalysisResult, ReplayAnalyzerRequestData, ReplayAnalysisViewerData } from '../models/winco/laparoscopy-analysis-result.model';

/**
 * Servicio que gestiona los intentos de un usuario
 */
@Injectable({
  providedIn: 'root'
})
export class ReplayService
{
  public params:Parameter[] | undefined;

  constructor(private clientService: HttpClientService,
              private modalBoxService:ModalBoxService) {}

  initialize()
  {
    this.clientService.get<Parameter[]>("Parameters").then(parameters => this.params = parameters);
  }

  /**
   * Obtiene un intento a través de su identificado
   * @param id Identificador del intento
   * @returns Replay - Intento obtenido
   */
  public GetReplay(id: string): Promise<Replay>
  {
    return this.clientService.get<Replay>("Replays/Replay/" + id);
  }

  /**
   * Obtiene una lista de intentos
   * @param userUuid Opcional - Identificador de usuario
   * @param exerciseUuid Opcional - Identificador de ejercicio
   * @param courseUuid Opcional - Identificador de curso
   * @returns Replay[] - Lista de intentos obtenida
   */
  public GetReplays(idUser: string='', idExercise: string='', idCouse: string='', idGroup: string='', idCategory: string='', idSpeciality: string=''): Promise<Replay[]>
  {
    if(!idUser)
      idUser = environment.guidEmpty;

    if(!idExercise)
      idExercise = environment.guidEmpty;

    if(!idCouse)
      idCouse = environment.guidEmpty;

    if(!idGroup)
      idGroup = environment.guidEmpty;

    if(!idCategory)
      idCategory = environment.guidEmpty;

    if(!idSpeciality)
      idSpeciality = environment.guidEmpty;

    return this.clientService.get<Replay[]>("Replays/Replays/" + idUser + "/" + idExercise + "/" + idCouse + "/" + idGroup + "/" + idCategory + "/" + idSpeciality);
  }

  GetReplaysCount(idUser:string, idExercise:string): Promise<number>
  {
    return new Promise<number>((resolve, reject) =>
    {
      this.clientService.get<SimpleResponse>("Replays/ReplaysCount/" + idUser + "/" + idExercise)
                        .then(response => resolve(Number(response.message)))
                        .catch(err => reject(err));
    });
  }

  /**
   * Obtiene la máxima puntuación y el mínimo tiempo de un ejercicio a partir de un identificador de intento
   * @param id Identificador del intento
   * @returns BetterScore - Mejor puntuación del ejercicio
   */
  public GetBetterScore(id: string): Promise<BetterScore>
  {
    return this.clientService.get<BetterScore>("Replays/BetterScore/" + id);
  }

  /**
   * Obtiene la puntuación del mejor intento de un ejercicio
   * @param idUser Identificador de usuario
   * @param idExercise Identificador de ejercicio
   * @param idCouse Identificador de curso
   * @returns BetterScore - Mejor puntuación del ejercicio
   */
  public GetBestReplay(idUser: string, idExercise: string, idCouse: string, idGroup: string, idCategory: string, idSpeciality: string): Promise<BetterScore>
  {
    return this.clientService.get<BetterScore>("Replays/BestReplay/" + idUser + "/" + idExercise + "/" + idCouse + "/" + idGroup + "/" + idCategory + "/" + idSpeciality);
  }

  /**
   * Obtiene la posición en el ranking de un usuario
   * @param idUser Identificador de usuario
   * @returns Promise
   */
  public GetPosition(idUser: string): Promise<SimpleResponse>
  {
    return this.clientService.get<SimpleResponse>("Replays/Position/" + idUser);
  }

  /**
   * Devuelve los mejores resultados par aun ejercicio
   * @param idUser Identificador de usuario
   * @param idExercise Identificador de ejercicio
   * @param idCouse Identificador de curso
   * @returns BestResults - Objeto con los mejores resultados
   */
  public GetBestResults(idUser: string, idExercise: string, idCouse: string, idGroup: string, idCategory: string, idSpeciality: string): Promise<BestResults>
  {
    return this.clientService.get<BestResults>("Replays/BestResults/" + idUser + "/" + idExercise + "/" + idCouse + "/" + idGroup + "/" + idCategory + "/" + idSpeciality);
  }

  /**
   * Guarda o actualiza una revisión de un intento
   * @param data Datos de la revisión
   * @returns Promise
   */
  public UpdateCheckList(data: ReplayCheckList): Promise<any>
  {
    return this.clientService.post<any>("Replays/UpdateCheckList", data);
  }

  /**
   * Devuelve el listado de paramaetros aunque no haya replay (por ejemplo para estadisticas sin tracking)
   */
  public GetParamsWithoutReplay()
  {
      //parámetros administrados en un intento (Replay)
    return [
      {field: 'speed', title: 'velocidad', unit: 'cm/s', value: 0, average: 0, valueText: '', css:''},
      {field: 'totalDistance', title: 'distancia_total', unit: 'cm', value: 0, average: 0, valueText: '', css:''},
      {field: 'totalDistanceLeft', title: 'distancia_izquierda', unit: 'cm', value: 0, average: 0, valueText: '', css:''},
      {field: 'totalDistanceRight', title: 'distancia_derecha', unit: 'cm', value: 0, average: 0, valueText: '', css:''},
    ];
  }

  // /**
  //  * Completa los parámetros de un intento y devuelve una copia del objeto
  //  * @param replay Intento a completar
  //  * @returns Parámetros del intento
  //  */
  // public GetCloneParams(replay: Replay)
  // {
  //   for (const param of this.params)
  //   {
  //     param.value = replay[param.field] ? replay[param.field] : "0";
  //     param.average = replay[param.field + 'Average'] ? replay[param.field + 'Average'] : "0.00";

  //     param.valueText = (param.value == null) ? "--" : param.value;

  //     if(param.valueText != '--')
  //     {
  //       switch(param.field)
  //       {
  //         case 'acceleration' :
  //         case 'offScreens' :
  //           param.css = (param.value < param.average) ? 'good' : ((param.value > param.average) ? 'bad' : '');
  //           break;

  //         case 'speed' :
  //         case 'smoothness' :
  //           param.css = (param.value > param.average) ? 'good' : ((param.value < param.average) ? 'bad' : '');
  //           break;

  //         // case 'totalDistance' :
  //         // case 'totalDistanceLeft' :
  //         // case 'totalDistanceRight' :
  //         // case 'distanceBetweenInstrument' :
  //         default:
  //           param.css = '';
  //           break;
  //       }
  //     }
  //   }

  //   return params;

  //   /*
  //   for (const average of replay.averages)
  //   {
  //     // param.value = param.value ?? 0;
  //     // param.average = replay[param.field + 'Average'] ? replay[param.field + 'Average'] : "0.00";

  //     const parameter = replay.replayParameters.find(m => m.parameter.idParameter === average.parameter.idParameter)

  //     average.valueText = (average.value == null) ? "--" : average.value.toString();

  //     if(average.valueText != '--')
  //     {
  //       switch(average.parameter.idParameter)
  //       {
  //         case EParameter.ACCELERATION :
  //         case EParameter.OFFSCREENS :
  //           average.css = (parameter.value < average.value) ? 'good' : ((parameter.value > average.value) ? 'bad' : '');
  //           break;

  //         case EParameter.SPEED :
  //         case EParameter.SMOOTHNES :
  //           average.css = (parameter.value > average.value) ? 'good' : ((parameter.value < average.value) ? 'bad' : '');
  //           break;

  //         // case 'totalDistance' :
  //         // case 'totalDistanceLeft' :
  //         // case 'totalDistanceRight' :
  //         // case 'distanceBetweenInstrument' :
  //         default:
  //           average.css = '';
  //           break;
  //       }
  //     }
  //   }

  //   return replay;
  //   */
  // }

  /**
   * Exporta un conjunto de intentos
   * @param data Parámetros de exportación
   * @returns Promise
   */
  public Export(data: ExportForm): Promise<SimpleResponse>
  {
    return this.clientService.post<SimpleResponse>("Replays/Export", data);
  }

  /**
   * Elimina un fichero exportado
   * @param data Parámetros de exportación
   * @returns Promise
   */
  public ClearExport(data: ExportForm): Promise<any>
  {
    return this.clientService.post<any>("Replays/ClearExport", data);
  }

  /**
   * Analiza un vídeo de un replay
   * @returns Replay - Intento que se va a analizar
   */
  public AnalyzeVideo(uuid: string): Promise<LaparoscopyAnalysisResult>
  {
    return this.clientService.get<LaparoscopyAnalysisResult>("ChatGPT/AnalyzeVideo/" + uuid);
  }

  /**
   * Exporta a pdf un analisis IA de un replay
   * @returns Replay - Intento que se va a analizar
   */
  public exportAnalysis(uuid: string): Promise<SimpleResponse>
  {
    return this.clientService.get<SimpleResponse>("ChatGPT/ExportAnalysis/" + uuid);
  }

  openExportAnalysis(uuid: string)
  {
    this.modalBoxService.show(new ModalBoxData({
       component: ReplayAnalizerExportComponent,
       autoHide: false,
       outCloser: false,
       data: uuid
    }));
  }


  openAnalyzer(uuid: string, score: number | null = null)
  {
    const data: ReplayAnalyzerRequestData = { uuid, score };

    this.modalBoxService.show(new ModalBoxData({
       component: ReplayAnalyzerComponent,
       autoHide: false,
       outCloser: false,
       data
    }));
  }

  viewAnalysis(raw: string | LaparoscopyAnalysisResult, score: number | null = null)
  {
    const analysis: LaparoscopyAnalysisResult = (typeof(raw) === 'string') ? JSON.parse(raw) : raw;
    const data: ReplayAnalysisViewerData = { analysis, score };

    this.modalBoxService.show(new ModalBoxData({
       component: ReplayViewerAnalysisComponent,
       autoHide: true,
       outCloser: true,
       close: new ModalBoxClose({ event: ModalBoxEvent.CANCEL }),
       width:'75em',
       height:'50em',
       data
    }));
  }
}
